import { FormContainer } from '@wyz/ui';
import { useOnUpdate } from '@wyz/utils';
import { memo, useCallback, useEffect, useEffectEvent } from 'react';
import { DefaultValues, FieldValues, useForm } from 'react-hook-form';
import { IUserCustomer } from '~/common/types';
import { IBaseFilter } from '~/purchase/types';

type Props<TFilter extends FieldValues> = React.PropsWithChildren<{
  defaultValues: TFilter;
  onFilter: (payload: { filters: TFilter }) => void;
  customer: IUserCustomer;
}>;

function FilterBarFormContainer<TFilter extends FieldValues = IBaseFilter>({
  defaultValues,
  onFilter,
  children,
  customer,
}: Props<TFilter>) {
  const methods = useForm<TFilter>({
    defaultValues: defaultValues as DefaultValues<TFilter>,
  });

  const resetForm = useEffectEvent(() => {
    methods.reset();
  });

  useOnUpdate(() => {
    if (customer.id) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      resetForm();
    }
  }, [customer.id]);

  const handleOnsubmit = useCallback(
    (formData: TFilter) => {
      onFilter({ filters: formData });
    },
    [onFilter],
  );

  useEffect(() => {
    const subscription = methods.watch(() =>
      methods.handleSubmit(handleOnsubmit)(),
    );
    return () => subscription.unsubscribe();
  }, [methods, onFilter, handleOnsubmit]);

  return (
    <FormContainer
      methods={methods}
      submitHandler={handleOnsubmit}
      className='w-100'
    >
      {children}
    </FormContainer>
  );
}

export default memo(FilterBarFormContainer) as typeof FilterBarFormContainer;
